import { useEffect, useMemo, useRef, useState } from 'react'
import PackCardQuietStack from '../RecipePacks/PackCardQuietStack.jsx'
import PackCardCompact from '../RecipePacks/PackCardCompact.jsx'
import PackDetailModal from '../RecipePacks/PackDetailModal.jsx'
import PackDetailSheet from '../RecipePacks/PackDetailSheet.jsx'
import {
  packTelemetryIds,
  plannerPackSurfaceFromMode,
  trackRecipePackEvent,
  trackRecipePackShelfViewedOnce,
} from '../../utils/recipePackAnalytics.js'
import '../RecipePacks/RecipePacks.css'
import './planner.css'

/**
 * RecipePackShelf — curated packs on the Plan screen (cold-start hero + thin-library shelf).
 * Uses the same card components and wrapping grids as Recipes → Add.
 *
 * @param {'hero'|'shelf'|'none'} mode
 * @param {object[]} packs — from resolveCuratedPacks()
 * @param {Set<string>} [addedPackIds]
 * @param {(pack: object) => void | Promise<void>} [onApplyPack]
 * @param {() => void} [onBrowseAllPacks]
 * @param {boolean} [isMobile]
 */
export default function RecipePackShelf({
  mode,
  packs: packsProp,
  addedPackIds = new Set(),
  onBrowseAllPacks,
  onApplyPack,
  isMobile = false,
}) {
  const [openPackId, setOpenPackId] = useState(null)
  const shelfViewedRef = useRef(new Set())
  const packs = useMemo(() => packsProp ?? [], [packsProp])
  const surface = plannerPackSurfaceFromMode(mode)

  const packById = useMemo(
    () =>
      Object.fromEntries(
        packs.flatMap((p) => [
          [p.id, p],
          [p.slug, p],
        ].filter(([key]) => key)),
      ),
    [packs],
  )

  useEffect(() => {
    if (mode === 'none' || !mode || packs.length === 0) return
    trackRecipePackShelfViewedOnce(
      surface,
      { pack_count: packs.length },
      shelfViewedRef,
    )
  }, [mode, packs.length, surface])

  if (mode === 'none' || !mode) return null

  const isHero = mode === 'hero'
  if (packs.length === 0) return null

  const actionsEnabled = typeof onApplyPack === 'function'

  const openPackDetail = (packId) => {
    const pack = packById[packId]
    if (!pack) return
    setOpenPackId(packId)
    const packKeys = [pack.id, pack.slug, packId].filter(Boolean)
    const alreadyAdded = packKeys.some((key) => addedPackIds.has(key))
    trackRecipePackEvent('recipe_pack_detail_opened', {
      surface,
      ...packTelemetryIds(pack),
      recipe_count: Array.isArray(pack.recipes) ? pack.recipes.length : Number(pack.n) || 0,
      already_added: alreadyAdded,
    })
  }

  const openPack = openPackId ? packById[openPackId] : null
  const openPackAdded = openPack
    ? addedPackIds.has(openPack.id) || (openPack.slug && addedPackIds.has(openPack.slug))
    : false

  return (
    <>
      <div className={`pack-shelf${isHero ? ' pack-shelf--hero' : ' pack-shelf--default'}`}>
        <div className="pack-shelf-header">
          <span className="pack-shelf-label">
            {isHero ? 'Recipe packs · pick 1 to start' : 'Recipe packs · add a few more'}
          </span>
          <button
            type="button"
            className="planner-btn btn-ghost pack-shelf-preview-btn"
            onClick={onBrowseAllPacks}
            disabled={!onBrowseAllPacks}
            title={onBrowseAllPacks ? 'See all packs in Recipes' : undefined}
          >
            Browse all packs →
          </button>
        </div>

        <div className={isHero ? 'rp-pack-grid-large' : 'rp-pack-grid-mini'}>
          {packs.map((pack) => {
            const added =
              addedPackIds.has(pack.id) || (pack.slug && addedPackIds.has(pack.slug))

            if (isHero) {
              return (
                <PackCardQuietStack
                  key={pack.id}
                  pack={pack}
                  added={added}
                  onOpen={() => openPackDetail(pack.id)}
                  onAdd={
                    actionsEnabled
                      ? () => onApplyPack?.(pack)
                      : undefined
                  }
                />
              )
            }

            return (
              <PackCardCompact
                key={pack.id}
                pack={pack}
                added={added}
                variant="desktop"
                onOpen={() => openPackDetail(pack.id)}
                onAdd={actionsEnabled ? () => onApplyPack?.(pack) : undefined}
              />
            )
          })}
        </div>
      </div>

      {openPack && !isMobile && (
        <PackDetailModal
          pack={openPack}
          added={openPackAdded}
          onClose={() => setOpenPackId(null)}
          onAdd={() => {
            setOpenPackId(null)
            onApplyPack?.(openPack)
          }}
        />
      )}

      {openPack && isMobile && (
        <PackDetailSheet
          pack={openPack}
          added={openPackAdded}
          onClose={() => setOpenPackId(null)}
          onAdd={() => {
            setOpenPackId(null)
            onApplyPack?.(openPack)
          }}
        />
      )}
    </>
  )
}
